import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, ShieldAlert, ShieldX, RefreshCw, Loader2, ArrowLeft, Key, Zap, Users, Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { toast } from "sonner";

// Iter 113 — Admin env-health dashboard.
// Purpose: bookmarkable one-tap post-deploy sanity check. Wraps
// `GET /api/admin/env-health` in a coloured status card so an admin
// (on phone, right after triggering a redeploy) can see at a glance
// whether the critical DELTA_KEK env var is wired.
//
// Three states, three colours:
//   • ok                         → green (var(--bt-win))
//   • warning_no_key_but_no_users → amber (var(--bt-warn))
//   • data_disaster_pending      → red   (var(--bt-loss))
//
// Non-admin visitors get 403 → we render a friendly "admin only"
// message rather than a raw stack trace.

const STATE_UI = {
  ok: {
    color: "var(--bt-win)",
    icon: ShieldCheck,
    label: "All clear",
    tone: "positive",
  },
  warning_no_key_but_no_users: {
    color: "var(--bt-warn)",
    icon: ShieldAlert,
    label: "Config warning",
    tone: "warning",
  },
  data_disaster_pending: {
    color: "var(--bt-loss)",
    icon: ShieldX,
    label: "IMMEDIATE ACTION",
    tone: "critical",
  },
};

export default function AdminHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/env-health");
      setData(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) setError({ kind: "forbidden" });
      else if (status === 401) setError({ kind: "unauthenticated" });
      else setError({ kind: "network", detail: e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const refresh = () => {
    fetchHealth();
    toast.success("Refreshed.");
  };

  return (
    <div data-testid="admin-health-page" className="space-y-6 max-w-2xl">
      <div className="space-y-1">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] transition-colors"
          data-testid="admin-health-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Dashboard
        </Link>
        <h1 className="font-heading text-2xl sm:text-3xl">Admin — Environment health</h1>
        <p className="text-sm text-[var(--bt-text-muted)]">
          Post-deploy sanity check for the critical Delta encryption key + sync scheduler config.
        </p>
      </div>

      {loading && !data && <LoadingCard />}
      {error && <ErrorCard error={error} onRetry={fetchHealth} />}
      {data && (
        <>
          <StatusCard data={data} onRefresh={refresh} loading={loading} />
          <DetailsGrid data={data} />
          <GuardrailCard guardrail={data.read_only_guardrail} />
          <FootNote checkedAt={data.checked_at} />
        </>
      )}
    </div>
  );
}

// ─── Loading / error states ─────────────────────────────────────────

function LoadingCard() {
  return (
    <section
      data-testid="admin-health-loading"
      className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-8 flex items-center justify-center gap-3"
    >
      <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--bt-accent)" }} />
      <span className="text-sm text-[var(--bt-text-muted)]">Checking environment…</span>
    </section>
  );
}

function ErrorCard({ error, onRetry }) {
  const copy = {
    forbidden: {
      headline: "Admin only",
      body: "This page is restricted to accounts with the admin role. If you should have access, ask an existing admin to update your role.",
    },
    unauthenticated: {
      headline: "Sign in required",
      body: "Log in with an admin account to see the environment health check.",
    },
    network: {
      headline: "Couldn't reach the backend",
      body: `The health endpoint didn't respond (${error.detail || "unknown error"}). Retry in a moment — if it keeps failing, check Emergent's status page or the pod logs.`,
    },
  }[error.kind] || { headline: "Something went wrong", body: "" };

  return (
    <section
      data-testid="admin-health-error"
      className="bt-card border rounded-sm p-6 space-y-3"
      style={{
        borderColor: "color-mix(in srgb, var(--bt-loss) 40%, var(--bt-border))",
        background: "color-mix(in srgb, var(--bt-loss) 5%, var(--bt-surface))",
      }}
    >
      <div className="flex items-start gap-3">
        <ShieldX className="w-6 h-6 shrink-0" style={{ color: "var(--bt-loss)" }} />
        <div>
          <h2 className="font-heading text-lg">{copy.headline}</h2>
          <p className="text-sm text-[var(--bt-text-muted)] mt-1">{copy.body}</p>
        </div>
      </div>
      {error.kind === "network" && (
        <Button
          onClick={onRetry}
          data-testid="admin-health-error-retry"
          className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-semibold"
        >
          <RefreshCw className="w-4 h-4 mr-1.5" /> Try again
        </Button>
      )}
    </section>
  );
}

// ─── Status card (the coloured signal) ──────────────────────────────

function StatusCard({ data, onRefresh, loading }) {
  const ui = STATE_UI[data.state] || STATE_UI.data_disaster_pending;
  const Icon = ui.icon;
  return (
    <section
      data-testid="admin-health-status-card"
      data-state={data.state}
      className="bt-card border rounded-sm p-6 space-y-4"
      style={{
        borderColor: `color-mix(in srgb, ${ui.color} 55%, var(--bt-border))`,
        background: `color-mix(in srgb, ${ui.color} 8%, var(--bt-surface))`,
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
          style={{ background: `color-mix(in srgb, ${ui.color} 22%, var(--bt-surface))` }}
        >
          <Icon className="w-7 h-7" style={{ color: ui.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="bt-label"
            style={{ color: ui.color }}
            data-testid="admin-health-state-label"
          >
            {ui.label}
          </p>
          <h2
            className="font-heading text-xl sm:text-2xl mt-0.5"
            data-testid="admin-health-state-name"
          >
            {data.state}
          </h2>
          <p
            className="text-sm text-[var(--bt-text)] mt-1.5 leading-relaxed"
            data-testid="admin-health-message"
          >
            {data.message}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end">
        <Button
          variant="outline"
          onClick={onRefresh}
          disabled={loading}
          data-testid="admin-health-refresh"
          className="border-[var(--bt-border)]"
        >
          {loading
            ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Re-check
        </Button>
      </div>
    </section>
  );
}

// ─── Details grid ───────────────────────────────────────────────────

function DetailsGrid({ data }) {
  const kek = data.delta_kek || {};
  const conn = data.delta_connections || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <DetailCard
        testid="admin-health-kek-card"
        icon={Key}
        label="Envelope encryption key"
        value={kek.configured ? "Configured" : "MISSING"}
        valueColor={kek.configured ? "var(--bt-win)" : "var(--bt-loss)"}
        subtitle={kek.configured
          ? `${kek.length_chars} chars — expected 44 for a base64-encoded 32-byte key.${kek.length_chars !== 44 ? " ⚠ Unexpected length." : ""}`
          : "DELTA_KEK env var is not set on this pod."}
      />
      <DetailCard
        testid="admin-health-sync-card"
        icon={Zap}
        label="Sync scheduler interval"
        value={`${data.delta_sync_interval_min_env} min`}
        valueColor="var(--bt-text)"
        subtitle="Set via DELTA_SYNC_INTERVAL_MIN. Min-floor 60s enforced at boot."
      />
      <DetailCard
        testid="admin-health-conn-total"
        icon={Users}
        label="Delta connections (total)"
        value={String(conn.total ?? 0)}
        valueColor="var(--bt-text)"
        subtitle={`${conn.active ?? 0} active · ${conn.error ?? 0} in error state`}
      />
      <DetailCard
        testid="admin-health-conn-error"
        icon={ShieldAlert}
        label="Connections in error"
        value={String(conn.error ?? 0)}
        valueColor={(conn.error ?? 0) > 0 ? "var(--bt-loss)" : "var(--bt-text-muted)"}
        subtitle={(conn.error ?? 0) > 0
          ? "Investigate — 5+ consecutive sync failures or 401 (revoked key)."
          : "No user needs re-connect action."}
      />
    </div>
  );
}

function DetailCard({ testid, icon: Icon, label, value, valueColor, subtitle }) {
  return (
    <div
      data-testid={testid}
      className="rounded-md border border-[var(--bt-border)] p-4 space-y-1.5"
      style={{ background: "var(--bt-surface-2)" }}
    >
      <div className="flex items-center gap-2 text-xs text-[var(--bt-text-muted)] uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p
        className="font-mono text-xl tabular-nums"
        style={{ color: valueColor }}
      >
        {value}
      </p>
      <p className="text-xs text-[var(--bt-text-muted)] leading-relaxed">
        {subtitle}
      </p>
    </div>
  );
}

// ─── Read-only guardrail card ────────────────────────────────────
// iter133 6.1 item 2 — honest surfacing of how we enforce Delta
// read-only status. Verified 2026-02-14 that Delta India publishes
// NO per-key permission metadata endpoint. Our guarantee is:
// _guard + attestation. Admins must be able to see this without
// opening code.

function GuardrailCard({ guardrail }) {
  if (!guardrail) return null;
  const verifiable = !!guardrail.verifiable_against_delta_metadata;
  return (
    <section
      data-testid="admin-health-guardrail-card"
      className="rounded-md border border-[var(--bt-border)] p-5 space-y-4"
      style={{ background: "var(--bt-surface-2)" }}
    >
      <div className="flex items-start gap-3">
        <Lock className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--bt-accent)" }} />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="bt-label">Read-only guardrail</p>
            <span
              data-testid="admin-health-guardrail-verifiable"
              className="text-xs font-mono uppercase px-2 py-0.5 rounded-sm"
              style={{
                background: verifiable ? "var(--bt-win)" : "var(--bt-surface)",
                color: verifiable ? "black" : "var(--bt-text-muted)",
                border: verifiable ? "none" : "1px solid var(--bt-border)",
              }}
            >
              {verifiable
                ? "Verifiable against Delta"
                : "Not verifiable against Delta"}
            </span>
          </div>
          <p className="text-sm mt-1">
            {guardrail.reason}
          </p>
        </div>
      </div>

      <div className="space-y-2" data-testid="admin-health-guardrail-enforced-by">
        <p className="bt-label">Enforced by</p>
        <ul className="space-y-1.5">
          {(guardrail.enforced_by || []).map((line, i) => (
            <li
              key={i}
              className="text-sm flex items-start gap-2 leading-relaxed"
            >
              <ShieldCheck
                className="w-3.5 h-3.5 mt-0.5 shrink-0"
                style={{ color: "var(--bt-win)" }}
              />
              <span className="font-mono text-xs">{line}</span>
            </li>
          ))}
        </ul>
      </div>

      <div data-testid="admin-health-guardrail-allowlist" className="space-y-2">
        <p className="bt-label">
          REST allowlist ({(guardrail.allowed_rest_paths || []).length} GET paths)
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(guardrail.allowed_rest_paths || []).map((p) => (
            <code
              key={p}
              className="text-xs font-mono px-2 py-1 rounded-sm"
              style={{
                background: "var(--bt-surface)",
                border: "1px solid var(--bt-border)",
              }}
            >
              GET {p}
            </code>
          ))}
        </div>
        <p className="text-xs text-[var(--bt-text-muted)]">
          Any HTTP method other than GET, or any path not on this list,
          raises <code>DeltaSecurityError</code> with a{" "}
          <code>[SECURITY-ALERT]</code> log line — before a socket is
          ever opened.
        </p>
      </div>

      {guardrail.liveness_probe_endpoint && (
        <p className="text-xs text-[var(--bt-text-muted)]">
          Liveness of the read allowlist can be verified per-connection
          at{" "}
          <code data-testid="admin-health-guardrail-liveness-endpoint">
            {guardrail.liveness_probe_endpoint}
          </code>{" "}
          (admin-only). It confirms the key has AT LEAST read permission
          by hitting <code>/v2/wallet/balances</code>.
        </p>
      )}
    </section>
  );
}

function FootNote({ checkedAt }) {
  const local = checkedAt
    ? new Date(checkedAt).toLocaleString("en-IN",
        { hour: "numeric", minute: "2-digit", second: "2-digit",
          day: "numeric", month: "short", year: "numeric" })
    : null;
  return (
    <p
      className="text-xs text-[var(--bt-text-muted)]"
      data-testid="admin-health-checked-at"
    >
      Last checked {local ? <><b>{local}</b></> : "just now"}.
      This page never displays or logs the actual DELTA_KEK value — only its presence bit and length.
    </p>
  );
}
